import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { clearSessionInvalidated, clearAuth } from '../../store/authSlice';
import './SessionInvalidatedModal.css';

interface SessionInvalidatedModalProps {
  /** Custom callback when user acknowledges the modal */
  onAcknowledge?: () => void;
}

export const SessionInvalidatedModal: React.FC<SessionInvalidatedModalProps> = ({
  onAcknowledge,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sessionInvalidatedReason } = useAppSelector((state) => state.auth);
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Listen for session-invalidated custom event from API clients
  useEffect(() => {
    const handleSessionInvalidated = (event: CustomEvent) => {
      const reason = event.detail?.reason || 'unknown';
      console.log('🔒 Session invalidated event received:', reason);
      setIsVisible(true);
      setCountdown(10);
    };

    window.addEventListener('session-invalidated', handleSessionInvalidated as EventListener);

    return () => {
      window.removeEventListener('session-invalidated', handleSessionInvalidated as EventListener);
    };
  }, []);

  // Also show if sessionInvalidatedReason is set in Redux
  useEffect(() => {
    if (sessionInvalidatedReason) {
      setIsVisible(true);
      setCountdown(10);
    }
  }, [sessionInvalidatedReason]);

  // Countdown timer for auto-redirect
  useEffect(() => {
    if (!isVisible) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleRedirectToLogin();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible]);

  const handleRedirectToLogin = useCallback(() => {
    dispatch(clearAuth());
    dispatch(clearSessionInvalidated());
    setIsVisible(false);
    
    onAcknowledge?.();
    
    navigate('/login', { replace: true });
  }, [dispatch, navigate, onAcknowledge]);

  if (!isVisible) {
    return null;
  }

  const getMessage = () => {
    switch (sessionInvalidatedReason) {
      case 'new_device':
        return 'You have been logged out because your account was accessed from another device or browser.';
      case 'session_expired':
        return 'Your session has expired. Please log in again to continue.';
      case 'security_logout':
        return 'You have been logged out for security reasons.';
      default:
        return 'Your session has ended. Please log in again to continue.';
    }
  };

  return (
    <div className="session-modal-overlay">
      <div className="session-modal">
        {/* Warning Icon */}
        <div className="session-modal-icon">
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Title */}
        <h2 className="session-modal-title">Session Ended</h2>

        {/* Message */}
        <p className="session-modal-message">{getMessage()}</p>

        {/* Security Note */}
        <div className="session-modal-note">
          <span className="note-icon">🔒</span>
          <span>For your security, only one active session is allowed at a time.</span>
        </div>

        {/* Action Button */}
        <button 
          className="session-modal-button"
          onClick={handleRedirectToLogin}
        >
          Go to Login ({countdown}s)
        </button>

        {/* Help Text */}
        <p className="session-modal-help">
          If you didn't log in from another device, please contact support.
        </p>
      </div>
    </div>
  );
};

export default SessionInvalidatedModal;

